import type {
  IncentiveEvidenceCode,
  SourceNodeIncentiveModel,
} from "@/lib/connectors/types";
import { systems } from "@/lib/systems";

const evidenceLabels: Record<IncentiveEvidenceCode, string> = {
  D: "Documented",
  P: "Partial",
  G: "Gap",
  C: "Claim",
  X: "Contested",
  I: "Inferred",
};

function money(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function title(value: string) {
  return value.replaceAll("_", " ");
}

function researchDepthDescription(
  depth: SourceNodeIncentiveModel["researchDepth"],
) {
  if (depth === "deep_dossier") {
    return "Entity-specific primary-source research with dated financial and governance records.";
  }
  if (depth === "evidence_enriched") {
    return "Native role graph enriched by existing entity-specific financial evidence.";
  }
  return "Native registry baseline; economics and wealth stay explicit gaps until primary evidence is added.";
}

export default function SourceIncentiveDossier({
  model,
}: {
  model: SourceNodeIncentiveModel;
}) {
  const canonicalSystemIds = Array.from(new Set(
    model.positions.flatMap((position) => position.systemIds),
  ));
  const candidateSystems = Array.from(new Set(
    model.positions.flatMap((position) => position.candidateSystemNames),
  ));
  const systemById = new Map<string, (typeof systems)[number]>(
    systems.map((system) => [system.id, system]),
  );
  const evidenceById = new Map(model.evidence.map((evidence) => [evidence.id, evidence]));

  return (
    <section
      className="source-incentive-dossier"
      data-research-depth={model.researchDepth}
      aria-label={`${model.sourceNodeName} system participation and economic incentive model`}
    >
      <div className="interests-reading"><div><h3>Observed interests</h3>{model.positions.map(position => <details key={position.id}><summary><strong>{position.targetName}</strong><span>{evidenceLabels[position.evidenceCode]}</span></summary><p>{position.role}</p><p className="muted">{position.state}</p></details>)}<h3>Possible incentives</h3><p className="muted">Interpretation</p>{model.incentiveFactors.map(factor => <details key={factor.id}><summary>{factor.label}</summary><p>{factor.description}</p><ul>{factor.countervailingFactors.map(item => <li key={item}>{item}</li>)}</ul></details>)}</div><aside><h3>Evidence gaps</h3>{model.unknowns.slice(0,3).map(item => <p key={item}>{item}</p>)}<h3>Net worth</h3><p>{model.netWorth.headline}</p><p className="muted">{title(model.netWorth.status)}</p></aside></div>
      <details className="full-incentive-model"><summary>Full model and source records</summary>
      <header className="incentive-dossier-heading">
        <div>
          <span>Source-node incentive model · schema v{model.schemaVersion}</span>
          <h3>Systems, authority, and economics</h3>
          <p>{model.thesis}</p>
          <small className="research-depth-note">
            {title(model.researchDepth)} · {researchDepthDescription(model.researchDepth)}
          </small>
        </div>
        <div>
          <span className={`evidence-code code-${model.status === "mapped" ? "D" : "P"}`}>{model.status === "mapped" ? "D" : "P"}</span>
          <span>{title(model.researchDepth)} · {model.status} · as of {model.asOf}</span>
        </div>
      </header>

      <div className="incentive-dossier-metrics">
        <div><span>Positions</span><strong>{model.positions.length}</strong></div>
        <div><span>Economic ties</span><strong>{model.economicTies.length}</strong></div>
        <div><span>Canonical systems</span><strong>{canonicalSystemIds.length}</strong></div>
        <div><span>Evidence records</span><strong>{model.evidence.length}</strong></div>
      </div>

      <section className="actor-system-overview" aria-labelledby={`${model.sourceNodeId}-system-map`}>
        <header>
          <div>
            <span>Participation graph</span>
            <h4 id={`${model.sourceNodeId}-system-map`}>Observed systems → actor → positions</h4>
          </div>
          <small>Roles are participation edges, not proof of motive or unilateral control.</small>
        </header>
        <div className="actor-system-map">
          <div className="actor-system-column system-nodes" aria-label="Linked canonical systems">
            <span>Systems</span>
            {canonicalSystemIds.map((systemId) => (
              <article key={systemId}>
                <small>{systemById.get(systemId)?.type ?? "Candidate system"}</small>
                <strong>{systemById.get(systemId)?.name ?? systemId}</strong>
                <span>{model.positions.filter((position) => position.systemIds.includes(systemId)).length} positions</span>
              </article>
            ))}
          </div>
          <div className="actor-node-column">
            <span>Source actor</span>
            <article>
              <small>{model.actorKind}</small>
              <strong>{model.sourceNodeName}</strong>
              <span>{model.status} coverage</span>
            </article>
          </div>
          <div className="actor-system-column position-nodes" aria-label="Observed actor positions">
            <span>Positions</span>
            {model.positions.map((position) => (
              <article key={position.id}>
                <div>
                  <small>{position.state}</small>
                  <span className={`evidence-code code-${position.evidenceCode}`} title={evidenceLabels[position.evidenceCode]}>{position.evidenceCode}</span>
                </div>
                <strong>{position.targetName}</strong>
                <span>{position.role}</span>
              </article>
            ))}
          </div>
        </div>
        {candidateSystems.length > 0 && (
          <div className="candidate-system-strip">
            <span>System coverage gaps</span>
            <div>{candidateSystems.map((system) => <small key={system}>{system}</small>)}</div>
          </div>
        )}
      </section>

      <section className="economic-model" aria-labelledby={`${model.sourceNodeId}-economic-model`}>
        <header>
          <div>
            <span>Economic model</span>
            <h4 id={`${model.sourceNodeId}-economic-model`}>Ties, value coverage, and net worth</h4>
          </div>
          <small>Values are dated gross observations unless explicitly labeled as estimates.</small>
        </header>

        <div className="net-worth-model">
          <div className="net-worth-headline">
            <span>Estimated net worth</span>
            <strong>{model.netWorth.headline}</strong>
            <small>{title(model.netWorth.status)} · {model.netWorth.confidence} confidence</small>
          </div>
          <div className="observable-marks">
            <span>Observable gross asset marks</span>
            {model.netWorth.observableGrossAssetMarks.length ? (
              model.netWorth.observableGrossAssetMarks.map((mark) => (
                <article key={mark.id}>
                  <strong>{money(mark.value)}</strong>
                  <span>{mark.label}</span>
                  <small>{mark.methodology}</small>
                  <p>{mark.caveat}</p>
                </article>
              ))
            ) : (
              <p className="dossier-empty">
                No defensible dated asset mark is registered. No zero balance is implied.
              </p>
            )}
          </div>
          <details>
            <summary>Why the model refuses a total</summary>
            <div>
              <section>
                <span>Excluded from personal wealth</span>
                <ul>{model.netWorth.excludedFromPersonalNetWorth.map((item) => <li key={item}>{item}</li>)}</ul>
              </section>
              <section>
                <span>Unknown components</span>
                <ul>{model.netWorth.unknownComponents.map((item) => <li key={item}>{item}</li>)}</ul>
              </section>
            </div>
          </details>
        </div>

        <div className="economic-tie-ledger">
          {model.economicTies.length ? (
            model.economicTies.map((tie) => (
              <details key={tie.id}>
                <summary>
                  <span className={`evidence-code code-${tie.evidenceCode}`} title={evidenceLabels[tie.evidenceCode]}>{tie.evidenceCode}</span>
                  <strong>{tie.targetName}</strong>
                  <small>{title(tie.type)} · {title(tie.scope)}</small>
                  <span>{tie.disclosedValue ? money(tie.disclosedValue.amount) : "Value undisclosed"}</span>
                </summary>
                <div>
                  <p>{tie.statement}</p>
                  <dl>
                    <div><dt>Direction</dt><dd>{title(tie.direction)}</dd></div>
                    <div><dt>Liquidity</dt><dd>{title(tie.liquidity)}</dd></div>
                    <div><dt>Confidence</dt><dd>{tie.confidence}</dd></div>
                    {tie.disclosedValue && <div><dt>Value basis</dt><dd>{tie.disclosedValue.basis}</dd></div>}
                  </dl>
                  <small>{tie.limitation}</small>
                  <div className="dossier-evidence-links">
                    {tie.evidenceIds.map((evidenceId) => {
                      const evidence = evidenceById.get(evidenceId);
                      return evidence ? <a href={evidence.url} key={evidenceId} target="_blank" rel="noreferrer">{evidence.label}</a> : null;
                    })}
                  </div>
                </div>
              </details>
            ))
          ) : (
            <p className="dossier-empty dossier-empty-ledger">
              No evidence-bounded economic tie is registered. This is a coverage gap, not evidence that the actor has no economic incentives.
            </p>
          )}
        </div>
      </section>

      <section className="incentive-factor-model" aria-labelledby={`${model.sourceNodeId}-factors`}>
        <header>
          <div>
            <span>Causal hypotheses</span>
            <h4 id={`${model.sourceNodeId}-factors`}>Incentive factors and counterweights</h4>
          </div>
          <small>Inferred effects remain hypotheses until behavior and tradeoffs support them.</small>
        </header>
        <div>
          {model.incentiveFactors.map((factor) => (
            <article key={factor.id}>
              <header>
                <span>{title(factor.category)} · {factor.effect}</span>
                <span className={`evidence-code code-${factor.evidenceCode}`} title={evidenceLabels[factor.evidenceCode]}>{factor.evidenceCode}</span>
              </header>
              <h5>{factor.label}</h5>
              <p>{factor.description}</p>
              <details>
                <summary>Counterweights</summary>
                <ul>{factor.countervailingFactors.map((item) => <li key={item}>{item}</li>)}</ul>
              </details>
            </article>
          ))}
        </div>
      </section>

      <details className="incentive-evidence-ledger">
        <summary>Evidence, gaps, and conflicts ({model.evidence.length} records)</summary>
        <div>
          <section>
            <h4>Evidence ledger</h4>
            {model.evidence.map((evidence) => (
              <a href={evidence.url} key={evidence.id} target="_blank" rel="noreferrer">
                <span className={`evidence-code code-${evidence.code}`}>{evidence.code}</span>
                <span><strong>{evidence.label}</strong><small>{evidence.note}</small></span>
              </a>
            ))}
          </section>
          <section>
            <h4>Unknowns</h4>
            <ul>{model.unknowns.map((unknown) => <li key={unknown}>{unknown}</li>)}</ul>
            <h4>Conflicts and temporal cautions</h4>
            <ul>{model.conflicts.map((conflict) => <li key={conflict}>{conflict}</li>)}</ul>
          </section>
        </div>
      </details>

      <footer className="evidence-legend">
        {(Object.entries(evidenceLabels) as Array<[IncentiveEvidenceCode, string]>).map(([code, label]) => (
          <span key={code}><i className={`evidence-code code-${code}`}>{code}</i>{label}</span>
        ))}
      </footer>
      </details>
    </section>
  );
}
